import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Panel, Toolbar } from './Page';
import { SelectField } from './Form';
import { Chip } from './Chips';
import { EmptyState, ErrorState, TableSkeleton } from './Feedback';
import { TableWrap } from './Table';
import { SortableTh } from './SortableTh';
import { Pagination } from './Pagination';
import { useAuth } from '../lib/auth';
import { partnerPayments } from '../lib/endpoints';
import { useAsync } from '../lib/hooks';
import { useSortableTable } from '../lib/useSortableTable';
import { useToast } from '../lib/toast';
import { errorMessage } from '../lib/api';
import { formatDate, formatMoney, formatRelative } from '../lib/format';
import type { PartnerPaymentNotificationDto } from '../lib/types';

type NoticeColumn = 'direction' | 'partner' | 'invoice' | 'payment' | 'amount' | 'status' | 'when';

function noticeSortValue(n: PartnerPaymentNotificationDto, key: NoticeColumn): string | number {
  switch (key) {
    case 'direction':
      return n.direction;
    case 'partner':
      return n.counterpartyCompanyName ?? '';
    case 'invoice':
      return n.externalDocumentNo ?? n.sourcePostedSalesInvoiceNo ?? '';
    case 'payment':
      return n.paymentType ?? n.paymentStatus ?? '';
    case 'amount':
      return n.amount ?? 0;
    case 'status':
      return n.status;
    case 'when':
      return n.createdUtc;
    default:
      return '';
  }
}

const DIRECTION_OPTIONS = [
  { value: 'Inbound', label: 'Received — a buyer paid an invoice we sent' },
  { value: 'Outbound', label: 'Sent — we paid a partner’s bill' },
];

function statusTone(status: string): 'good' | 'warn' | 'bad' | 'neutral' {
  switch (status) {
    case 'Matched':
    case 'Published':
      return 'good';
    case 'Failed':
      return 'bad';
    case 'Unmatched':
    case 'Received':
      return 'warn';
    default:
      return 'neutral';
  }
}

/**
 * PAYMENT NOTICES — the payment leg of the invoice exchange, in both directions.
 *
 * Inbound: a connected buyer reported paying an invoice this company sent. It is matched to that
 * invoice by OUR document number on THAT connection; an unmatched notice is kept for a person to
 * look at, never guessed. Outbound: this company paid a bill that came from a partner's invoice in
 * QuickBooks, and the gateway told the seller — once per payment. Nothing here changes QuickBooks;
 * it reports what the accountant already did.
 */
export function PartnerPaymentNotices({ connectionId }: { connectionId: string | null }): JSX.Element {
  const { can } = useAuth();
  const toast = useToast();
  const canScan = can('partners.manage');

  const [direction, setDirection] = useState('');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [busy, setBusy] = useState(false);

  const list = useAsync(
    (signal) =>
      partnerPayments.list({ direction: direction || null, connectionId, page, pageSize }, signal),
    [direction, connectionId, page, pageSize],
  );

  const table = useSortableTable<NoticeColumn>({
    storageKey: 'partnerPaymentNoticesColumnWidths',
    columns: {
      direction: { minWidth: 110 },
      partner: { minWidth: 160 },
      invoice: { minWidth: 150 },
      payment: { minWidth: 140 },
      amount: { minWidth: 100 },
      status: { minWidth: 130 },
      when: { minWidth: 130 },
    },
  });

  const scan = async () => {
    setBusy(true);
    try {
      const outcome = await partnerPayments.scan();
      toast.success(
        outcome.message
          ?? `${outcome.published} payment notice(s) published, ${outcome.failed} failed, ${outcome.candidates} bill(s) considered.`,
      );
      list.reload();
    } catch (caught) {
      toast.error('Payment scan did not complete', errorMessage(caught));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Panel
      title="Payment notices"
      description="Payments partners reported against invoices this company sent, and payments this company reported to partners. Bill payments are scanned and reported automatically, and a notice the platform did not accept is retried until it does; the button forces a pass now."
      actions={
        canScan ? (
          <button type="button" className="btn btn-secondary btn-sm" disabled={busy} onClick={() => void scan()}>
            {busy ? 'Scanning…' : 'Report bill payments now'}
          </button>
        ) : undefined
      }
    >
      <Toolbar>
        <SelectField
          label="Direction"
          value={direction}
          onChange={(value) => {
            setDirection(value);
            setPage(1);
          }}
          options={DIRECTION_OPTIONS}
          placeholder="Both directions"
          className="toolbar-grow"
        />
      </Toolbar>

      {list.loading && !list.data ? (
        <TableSkeleton rows={3} columns={6} />
      ) : list.error ? (
        <ErrorState message="Payment notices could not be read." detail={list.error} onRetry={list.reload} />
      ) : !list.data || list.data.items.length === 0 ? (
        <EmptyState
          title="No payment notices"
          description="A notice appears when a buyer reports paying an invoice sent from here, or when a bill created from a partner's invoice is paid in QuickBooks and reported to the seller."
        />
      ) : (
        <>
          <TableWrap busy={list.loading} tableClassName="data-table-sortable">
            <colgroup>
              <col style={table.columnStyle('direction')} />
              <col style={table.columnStyle('partner')} />
              <col style={table.columnStyle('invoice')} />
              <col style={table.columnStyle('payment')} />
              <col style={table.columnStyle('amount')} />
              <col style={table.columnStyle('status')} />
              <col style={table.columnStyle('when')} />
            </colgroup>
            <thead>
              <tr>
                <SortableTh
                  label="Direction"
                  sortKey="direction"
                  currentSortKey={table.sortKey}
                  sortDir={table.sortDir}
                  onSort={table.toggleSort}
                  style={table.columnStyle('direction')}
                  onResizeStart={table.beginResize('direction')}
                />
                <SortableTh
                  label="Partner"
                  sortKey="partner"
                  currentSortKey={table.sortKey}
                  sortDir={table.sortDir}
                  onSort={table.toggleSort}
                  style={table.columnStyle('partner')}
                  onResizeStart={table.beginResize('partner')}
                />
                <SortableTh
                  label="Invoice"
                  sortKey="invoice"
                  currentSortKey={table.sortKey}
                  sortDir={table.sortDir}
                  onSort={table.toggleSort}
                  style={table.columnStyle('invoice')}
                  onResizeStart={table.beginResize('invoice')}
                />
                <SortableTh
                  label="Payment"
                  sortKey="payment"
                  currentSortKey={table.sortKey}
                  sortDir={table.sortDir}
                  onSort={table.toggleSort}
                  style={table.columnStyle('payment')}
                  onResizeStart={table.beginResize('payment')}
                />
                <SortableTh
                  label="Amount"
                  sortKey="amount"
                  currentSortKey={table.sortKey}
                  sortDir={table.sortDir}
                  onSort={table.toggleSort}
                  style={table.columnStyle('amount')}
                  onResizeStart={table.beginResize('amount')}
                />
                <SortableTh
                  label="Status"
                  sortKey="status"
                  currentSortKey={table.sortKey}
                  sortDir={table.sortDir}
                  onSort={table.toggleSort}
                  style={table.columnStyle('status')}
                  onResizeStart={table.beginResize('status')}
                />
                <SortableTh
                  label="When"
                  sortKey="when"
                  currentSortKey={table.sortKey}
                  sortDir={table.sortDir}
                  onSort={table.toggleSort}
                  style={table.columnStyle('when')}
                  onResizeStart={table.beginResize('when')}
                />
              </tr>
            </thead>
            <tbody>
              {table.sort(list.data.items, noticeSortValue).map((n) => (
                <tr key={n.id}>
                  <td>
                    <Chip tone="neutral">{n.direction === 'Inbound' ? 'Received' : 'Sent'}</Chip>
                  </td>
                  <td>
                    <span className="cell-strong">{n.counterpartyCompanyName ?? 'A connected partner'}</span>
                    <span className="cell-sub mono">{n.connectionId ?? ''}</span>
                  </td>
                  <td>
                    {n.externalDocumentNo ?? n.sourcePostedSalesInvoiceNo ?? '—'}
                    <span className="cell-sub">
                      {n.gatewayDocumentId ? (
                        <Link className="cell-link" to={`/purchase-invoices?document=${n.gatewayDocumentId}`}>
                          the bill
                        </Link>
                      ) : n.matchedOutboundDeliveryId ? (
                        'matched to a sent invoice'
                      ) : (
                        ''
                      )}
                    </span>
                  </td>
                  <td>
                    {n.paymentType ?? n.paymentStatus ?? '—'}
                    <span className="cell-sub">
                      {n.documentNo ?? ''}
                      {n.postingDate ? ` · ${formatDate(n.postingDate)}` : ''}
                    </span>
                  </td>
                  <td>{formatMoney(n.amount, n.currencyCode)}</td>
                  <td>
                    <Chip tone={statusTone(n.status)}>{n.status}</Chip>
                    {n.statusMessage ? <span className="cell-sub">{n.statusMessage}</span> : null}
                  </td>
                  <td>
                    {formatRelative(n.createdUtc)}
                    <span className="cell-sub">
                      {n.publishedUtc ? `published ${formatRelative(n.publishedUtc)}` : n.acknowledged ? 'acknowledged' : ''}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </TableWrap>

          <Pagination
            page={list.data.page}
            pageSize={list.data.pageSize}
            totalCount={list.data.totalCount}
            totalPages={list.data.totalPages}
            onPageChange={setPage}
            onPageSizeChange={(size) => {
              setPageSize(size);
              setPage(1);
            }}
          />
        </>
      )}
    </Panel>
  );
}
